/**
 * Tarjeta de cuenta, con su desplegable.

 *
 * VIVE EN DOS SITIOS y por eso admite `placement`:
 *
 *   · `sidebar` — pegada al fondo de la barra lateral, como en el maquetado. Se abre HACIA
 *     ARRIBA porque hacia abajo se saldría de la pantalla.
 *   · `bar` — compacta, en la barra superior, abriéndose hacia abajo y alineada a la derecha.
 *     Es la que ve el INQUILINO, que no tiene barra lateral: sin ella no había ningún sitio
 *     desde el que cerrar sesión y la única salida era borrar la cookie a mano.
 *
 * El chevron del maquetado prometía un menú que no existía: la tarjeta era un `div`
 * informativo con «Admin» escrito a mano. Ahora es el botón que abre el menú de cuenta y el
 * único sitio de la aplicación desde el que se cierra la sesión.
 *
 * QUIÉN ERES SALE DE LA SESIÓN, NO DE UNA CONSTANTE. Con el nombre fijo, el gestor que entra
 * con `gestion@naar.es` veía «Admin» en la esquina: da igual que la API aplique bien los
 * permisos si la interfaz te dice que eres otra persona. El menú enseña además el correo y la
 * organización, que es lo que se mira antes de pulsar «Cerrar sesión» en una máquina
 * compartida.
 *
 * ACCESIBILIDAD (patrón de menú, no de diálogo):
 *  · `aria-haspopup="menu"` + `aria-expanded` en el disparador.
 *  · `Esc` cierra y DEVUELVE el foco al disparador; un clic fuera cierra sin más.
 *  · Al abrir, el foco entra en la primera opción; las flechas recorren el menú.
 *  · No se atrapa el foco —tabular sale del menú y lo cierra—: un menú no es un diálogo, y
 *    atrapar el foco en algo que se abre por accidente deja al teclado encerrado.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROLE_LABELS } from '@naar/shared';
import { useAuth } from '../auth/AuthContext';
import { Icon } from './Icon';
import styles from './AccountMenu.module.css';

interface AccountMenuProps {
  placement?: 'sidebar' | 'bar';
}

export function AccountMenu({ placement = 'sidebar' }: AccountMenuProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    // El foco entra en la primera opción: quien abre el menú con el teclado quiere elegir.
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  /** Flechas dentro del menú, con vuelta al principio. */
  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [],
    );
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    items[(current + step + items.length) % items.length]?.focus();
  }

  async function handleSignOut() {
    // Dos peticiones seguidas serían peor que una: la segunda lleva un refresh token ya
    // rotado, y reutilizarlo se interpreta —con razón— como robo de sesión.
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } finally {
      /**
       * `logout()` limpia la sesión local pase lo que pase (ver `api/session.ts`), así que
       * aquí ya no hay usuario y este componente desaparece con la barra lateral. Se navega a
       * la raíz para no dejar una URL profunda de la sesión anterior esperando a la siguiente:
       * quien entre después no tiene por qué aterrizar en la ficha que estaba mirando otro.
       */
      navigate('/', { replace: true });
    }
  }

  if (!user) return null;

  const bar = placement === 'bar';

  return (
    <div className={bar ? `${styles.wrap} ${styles.wrapBar}` : styles.wrap} ref={wrapRef}>
      {open ? (
        <div
          className={bar ? `${styles.menu} ${styles.menuBar}` : styles.menu}
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label="Menú de cuenta"
          onKeyDown={onMenuKeyDown}
        >
          <div className={styles.header}>
            <div className={styles.headerName}>{user.fullName}</div>
            <div className={styles.headerEmail}>{user.email}</div>
            <div className={styles.headerMeta}>
              {ROLE_LABELS[user.role]} · {user.organization.name}
            </div>
          </div>

          <button
            type="button"
            role="menuitem"
            className={`${styles.item} ${styles.itemDanger}`}
            onClick={handleSignOut}
            disabled={signingOut}
          >
            <Icon name="logout" size={17} strokeWidth={2} />
            {signingOut ? 'Cerrando sesión…' : 'Cerrar sesión'}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        ref={triggerRef}
        data-tour="cuenta"
        className={[styles.trigger, bar ? styles.triggerBar : '', open ? styles.triggerOpen : '']
          .filter(Boolean)
          .join(' ')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(true) : setOpen(true))}
      >
        <span className={styles.avatar}>{user.avatarInitials}</span>
        <span className={styles.text}>
          <span className={styles.name}>{user.fullName}</span>
          <span className={styles.role}>{bar ? ROLE_LABELS[user.role] : 'Mi cuenta'}</span>
        </span>
        <Icon
          name="chevron-down"
          size={15}
          strokeWidth={2.2}
          color="var(--naar-text-muted)"
          className={open ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
        />
      </button>
    </div>
  );
}
